import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Descriptions,
  Empty,
  Space,
  Spin,
  Timeline,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import {
  downloadInvoice,
  fetchShipment,
  ShipmentProjection,
  TrackingEvent,
  TrackingDwellSummary,
} from '../api/kcc';
import CarrierTrackingNo from '../components/CarrierTrackingNo';
import { useNarrowScreen } from '../hooks/useNarrowScreen';
import { statusLabel } from '../utils/shipmentStatus';
import { formatTransportMode } from '../utils/transportMode';

const { Title, Paragraph, Text } = Typography;

function formatAddress(s: ShipmentProjection): string {
  const f = s.clientAddrFields || {};
  const parts = [f.name, f.companyName, f.addr, f.city, f.state, f.zip, f.tel, f.email]
    .filter(Boolean);
  if (parts.length > 0) {
    return parts.join(', ');
  }
  return s.clientAddr || '—';
}

/** 轨迹时间解析；无法解析时返回 0（排到后面） */
function parseTrackTime(time?: string): number {
  if (!time) return 0;
  const t = time.trim();
  const ms = Date.parse(t.replace(' ', 'T'));
  if (!Number.isNaN(ms)) return ms;
  const asNum = Number(t);
  return Number.isFinite(asNum) ? asNum : 0;
}

function formatDwellLine(e: TrackingEvent): string | null {
  if (e.dwellDays == null || Number.isNaN(Number(e.dwellDays))) return null;
  const days = Number(e.dwellDays);
  if (e.ongoing) {
    return `In progress · ${days} days`;
  }
  return `${days} days`;
}

function DwellSummaryBar({ summary }: { summary: TrackingDwellSummary }) {
  const n = summary.segmentCount ?? 0;
  if (n <= 0) return null;
  const max = summary.maxDwellDays;
  const label = summary.maxDwellLabel;
  return (
    <div
      style={{
        marginBottom: 16,
        padding: '8px 12px',
        background: '#fafafa',
        border: '1px solid #f0f0f0',
        borderRadius: 4,
        color: '#595959',
        fontSize: 12,
        lineHeight: 1.6,
        wordBreak: 'break-word',
      }}
    >
      {n} segment{n === 1 ? '' : 's'}
      {max != null && (
        <>
          <span style={{ margin: '0 8px', color: '#d9d9d9' }}>|</span>
          Longest dwell: {max} days{label ? ` @ ${label}` : ''}
        </>
      )}
    </div>
  );
}

const ShipmentDetailPage: React.FC = () => {
  const { shipmentId = '' } = useParams();
  const navigate = useNavigate();
  const narrow = useNarrowScreen();
  const sectionPad = narrow ? 12 : 20;
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [data, setData] = useState<ShipmentProjection | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const detail = await fetchShipment(shipmentId);
        if (!cancelled) setData(detail);
      } catch (e: any) {
        if (!cancelled) {
          message.error(e?.response?.data?.message || 'Shipment not found');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shipmentId]);

  const onDownload = async () => {
    if (!data?.validInvoice) return;
    setDownloading(true);
    try {
      await downloadInvoice(data.shipmentId, data.validInvoice.fileName);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return <Empty description="Shipment not found" />;
  }

  const tracking = data.trackingHistory;

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/shipments/list')}>
          Back
        </Button>
        <Title level={3} style={{ margin: 0, wordBreak: 'break-word' }}>
          {data.shipmentId}
        </Title>
      </Space>

      <section style={{ background: '#fff', padding: sectionPad, marginBottom: 16 }}>
        <Title level={5}>Shipment information</Title>
        <Descriptions column={narrow ? 1 : 2} size="small">
          <Descriptions.Item label="Status">{statusLabel(data.status)}</Descriptions.Item>
          <Descriptions.Item label="Shipping way">{formatTransportMode(data.transportMode)}</Descriptions.Item>
          <Descriptions.Item label="Carrier">{data.carrierName || '—'}</Descriptions.Item>
          <Descriptions.Item label="Carrier Tracking No.">
            <CarrierTrackingNo shipment={data} logoHeight={18} />
          </Descriptions.Item>
          <Descriptions.Item label="Cartons">{data.cartonCount ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Address" span={narrow ? 1 : 2}>
            <span style={{ wordBreak: 'break-word' }}>{formatAddress(data)}</span>
          </Descriptions.Item>
        </Descriptions>
      </section>

      <section
        style={{
          background: '#fff',
          padding: narrow ? 12 : 24,
          marginBottom: 16,
          minHeight: 280,
          borderLeft: '4px solid #1677ff',
        }}
      >
        <Title level={4} style={{ marginTop: 0 }}>
          Tracking
        </Title>
        {tracking?.mode === 'SUMMARY' ? (
          <Paragraph style={{ fontSize: 18, margin: '24px 0' }}>
            <Text strong>{tracking.summaryLabel || statusLabel(data.status)}</Text>
          </Paragraph>
        ) : tracking?.events && tracking.events.length > 0 ? (
          <>
            {tracking.dwellSummary && <DwellSummaryBar summary={tracking.dwellSummary} />}
            <Timeline
              style={{ marginTop: 8 }}
              items={[...tracking.events]
                .sort((a, b) => parseTrackTime(b.time) - parseTrackTime(a.time))
                .map((e, idx) => {
                  const entered = e.enteredAt || e.time || '—';
                  const leftLabel = e.ongoing ? 'In progress' : e.leftAt || '—';
                  const dwellLine = formatDwellLine(e);
                  return {
                    key: idx,
                    children: (
                      <div style={{ wordBreak: 'break-word' }}>
                        <div>
                          <Text type="secondary">{e.time || '—'}</Text>
                          {e.location ? <Text> · {e.location}</Text> : null}
                        </div>
                        <div>{e.description || '—'}</div>
                        <div style={{ fontSize: 12, color: '#666', lineHeight: 1.7, marginTop: 4 }}>
                          <div>Entered　{entered}</div>
                          <div>Left　{leftLabel}</div>
                          {dwellLine != null && <div>Dwell　{dwellLine}</div>}
                        </div>
                      </div>
                    ),
                  };
                })}
            />
          </>
        ) : (
          <Empty description="No tracking events yet" />
        )}
      </section>

      <section style={{ background: '#fff', padding: sectionPad }}>
        <Title level={5}>Invoice</Title>
        {data.validInvoice ? (
          <Space direction={narrow ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
            <Text style={{ wordBreak: 'break-word' }}>
              {data.validInvoice.fileName || data.validInvoice.invoiceId}
            </Text>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={onDownload}
              block={narrow}
            >
              Download
            </Button>
          </Space>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No invoice available" />
        )}
      </section>
    </div>
  );
};

export default ShipmentDetailPage;
